import { useSyncExternalStore } from "react";

export type CloudUser = {
  id: string;
  email: string;
  name: string;
};

export type ProjectMeta = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export type ProjectData = {
  elements: unknown[];
  appState: Record<string, unknown>;
  files: Record<string, unknown>;
};

export type SaveStatus = "idle" | "saving" | "saved" | "error";

const API_BASE: string = import.meta.env.VITE_APP_CLOUD_API_URL || "";

const TOKEN_KEY = "x3.cloud.token";
const USER_KEY = "x3.cloud.user";
const ACTIVE_KEY = "x3.cloud.activeProject";

type CloudState = {
  user: CloudUser | null;
  activeProject: ProjectMeta | null;
  status: SaveStatus;
  sessionExpired: boolean;
};

function readJsonStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJsonStorage(key: string, value: unknown) {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // storage cheio/indisponivel: ignora
  }
}

let state: CloudState = {
  user: readJsonStorage<CloudUser>(USER_KEY),
  activeProject: readJsonStorage<ProjectMeta>(ACTIVE_KEY),
  status: "idle",
  sessionExpired: false,
};

const listeners = new Set<() => void>();

function setState(patch: Partial<CloudState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getCloudState(): CloudState {
  return state;
}

export function getCloudToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function useCloudState(): CloudState {
  return useSyncExternalStore(subscribe, getCloudState);
}

async function request<T>(
  path: string,
  options: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  const token = getCloudToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  } catch {
    throw new Error("Sem conexão com o servidor.");
  }

  if (res.status === 401) {
    if (getCloudToken()) {
      logout();
      setState({ sessionExpired: true });
    }
    throw new Error("Sessão expirada. Entre novamente.");
  }

  let body: any = null;
  try {
    body = await res.json();
  } catch {
    // corpo vazio ou invalido
  }

  if (!res.ok) {
    throw new Error(body?.error || `Erro ${res.status}`);
  }
  return body as T;
}

export function setActiveProject(meta: ProjectMeta | null) {
  writeJsonStorage(ACTIVE_KEY, meta);
  setState({ activeProject: meta });
}

export function setSaveStatus(status: SaveStatus) {
  if (state.status !== status) {
    setState({ status });
  }
}

export function clearSessionExpiredFlag() {
  if (state.sessionExpired) {
    setState({ sessionExpired: false });
  }
}

export async function login(email: string, password: string) {
  const res = await request<{ token: string; user: CloudUser }>("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  localStorage.setItem(TOKEN_KEY, res.token);
  writeJsonStorage(USER_KEY, res.user);
  setState({ user: res.user, sessionExpired: false });
}

export async function register(email: string, password: string, name: string) {
  const res = await request<{ token: string; user: CloudUser }>(
    "/api/register",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, name }),
    },
  );
  localStorage.setItem(TOKEN_KEY, res.token);
  writeJsonStorage(USER_KEY, res.user);
  setState({ user: res.user, sessionExpired: false });
}

export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  writeJsonStorage(USER_KEY, null);
  setActiveProject(null);
  setState({ user: null });
}

export async function fetchMe(): Promise<void> {
  await request("/api/me");
}

export async function listProjects(): Promise<ProjectMeta[]> {
  const res = await request<{ projects: ProjectMeta[] }>("/api/projects");
  return res.projects;
}

export async function createProject(name: string): Promise<ProjectMeta> {
  const res = await request<{ project: ProjectMeta }>("/api/projects", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  return res.project;
}

export async function getProject(id: string): Promise<{
  project: ProjectMeta & { data: ProjectData };
}> {
  return request(`/api/projects/${encodeURIComponent(id)}`);
}

export async function saveProjectData(
  id: string,
  payload: ProjectData & { name?: string },
): Promise<{ ok: true; updatedAt: string; name: string }> {
  return request(`/api/projects/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function renameProject(id: string, name: string) {
  return request(`/api/projects/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
}

export async function deleteProject(id: string) {
  return request(`/api/projects/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/**
 * Abre o painel da conta. O handler real é registrado pelo componente de UI.
 */
let panelOpener: (() => void) | null = null;

export function registerPanelOpener(opener: () => void) {
  panelOpener = opener;
}

export function openAccountPanel() {
  panelOpener?.();
}
